import addressApi, {
  MUTATION_SYNC_PROPERTIES,
  QUERY_GET_PROPERTIES,
  QUERY_SYNC_PROPERTY_STATUS
} from "api/landlord/address";
import { useEffect, useState } from "react";

import { getErrorMessage } from "utils/error";
import { Input, InputSize } from "shared/components";
import { Layout } from "providers/Layout";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { DeletePropertyModal } from "shared/app/DeletePropertyModal";
import { PropertiesTable } from "./components/PropertiesTable";
import { LayoutButtons } from "./components/LayoutButtons";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { Pagination } from "shared/components/Pagination";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { PropertyManagementSystemSync } from "shared/components/PropertyManagementSystemSync";
import { PropertyManagementSystemSyncStatus } from "shared/types";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

const DEFAULT_PER_PAGE = Number(window.localStorage.getItem("per_page")) || 10;

export const Properties = () => {
  const dictionary = useUnitsPropertiesDictionary();
  const { type } = useAuthContext();
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    null
  );
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      property: ""
    },
    ["property"]
  );

  const [isSyncing, setIsSyncing] = useState(type.isPMS);
  const [debouncedProperty] = useDebouncedValue(searchParams.property, 500);

  const { data, refetch, isFetching } = useQuery({
    queryKey: [QUERY_GET_PROPERTIES, page, debouncedProperty],
    queryFn: () =>
      addressApi.getProperties({
        page,
        searchTerm: debouncedProperty,
        per_page: DEFAULT_PER_PAGE
      }),
    placeholderData: keepPreviousData
  });

  const { data: syncData, isError: syncError } =
    useQuery<PropertyManagementSystemSyncStatus>({
      queryKey: [QUERY_SYNC_PROPERTY_STATUS],
      queryFn: () =>
        addressApi.getPropertyManagementSystemSyncStatus("properties"),
      refetchInterval: 3000,
      enabled: isSyncing
    });

  const { mutate: handleRequestSync } = useMutation({
    mutationFn: async () => {
      return addressApi
        .requestPropertyManagementSystemSync("properties")
        .then(() => {
          toast.success("Sync properties requested successfully");
          setIsSyncing(true);
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
          setIsSyncing(false);
        });
    },
    mutationKey: [MUTATION_SYNC_PROPERTIES]
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedProperty]);

  useEffect(() => {
    setIsSyncing(type.isPMS);
  }, [type.isPMS]);

  useEffect(() => {
    const isSyncRunning = syncData?.status === "running";
    setIsSyncing(isSyncRunning);

    if (!isSyncRunning) refetch();
  }, [syncData, syncError]);

  return (
    <Layout
      footer={
        <div className="md:hidden flex ml-auto shrink-0 gap-2 md:w-auto w-full px-4 md:pl-4">
          <LayoutButtons onPropertyAdded={refetch} />
        </div>
      }
      hideFooterForDesktop
    >
      <div className="animate-appear">
        <div className="pb-4">
          <p className="m-0 mb-6 text-2xl font-bold text-content-1000">
            {dictionary.Properties}
          </p>
          <div className="flex justify-between gap-2 md:max-w-[calc(100vw-120px)] lg:max-w-[calc(100vw-280px)]">
            <Input
              id="search"
              label=""
              placeholder={`Search ${dictionary.properties}`}
              value={searchParams.property}
              onChange={(e) => setSearchParams({ property: e.target.value })}
              wrapperClassName="w-full md:w-auto"
              className="!mb-0 w-full lg:min-w-[280px] max-w-[376px] h-12 md:h-10"
              size={InputSize.SM}
            />
            <PropertyManagementSystemSync
              isSyncing={isSyncing}
              handleRequestSync={handleRequestSync}
              lastSuccessSyncedAt={syncData?.last_successful_sync}
            />
            <div className="ml-auto md:flex shrink-0 gap-2 md:w-auto w-full hidden">
              <LayoutButtons onPropertyAdded={refetch} />
            </div>
          </div>
        </div>

        <PropertiesTable
          data={data?.items || []}
          setSelectedPropertyId={setSelectedPropertyId}
          hasSearchParams={Boolean(debouncedProperty)}
          setShowDeletePropertyModal={setShowDeletePropertyModal}
          isLoading={isFetching}
          perPageRows={DEFAULT_PER_PAGE}
          viewPortHeightOffset={120}
          pagination={
            <div className="ml-auto flex md:max-w-[calc(100vw-106px)]">
              <Pagination
                className="md:justify-end justify-center"
                total={data?.page_count || 0}
                current={page}
                onChange={(p) => setPage(p)}
              />
            </div>
          }
        />
        <DeletePropertyModal
          opened={showDeletePropertyModal}
          onClose={() => setShowDeletePropertyModal(false)}
          propertyId={selectedPropertyId || ""}
          onSuccess={() => {
            refetch();
          }}
        />
      </div>
    </Layout>
  );
};
